import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { rechercher } from '../services/rechercheService';
import '../assets/styles/RechercheGlobale.css';

/**
 * Barre de recherche globale, en tête de chaque page du back-office.
 *
 * Elle cherche dans toutes les entités — demandes, devis, contrats,
 * journées, participants, clients, formateurs, formations, calendrier,
 * agenda — et sur toutes leurs colonnes utiles, sans casse ni accents.
 * Plusieurs mots : tous doivent se trouver (« dupont lyon »).
 *
 * Clavier : Ctrl+K (ou ⌘K) pour y aller, ↑ ↓ pour choisir, Entrée pour
 * ouvrir, Échap pour fermer.
 */
/** Émis par la loupe de la sidebar : le Layout ouvre la barre. */
export const EVENEMENT_RECHERCHE = 'gi:recherche';

/**
 * La barre n'est montée que lorsqu'on l'appelle (loupe de la sidebar ou
 * Ctrl+K, écoutés par le Layout) : fermée, elle ne prend aucune hauteur aux
 * tables des pages. `signalFocus` change à chaque nouvel appel pour redonner
 * le focus à une barre déjà ouverte ; `onFermer` la démonte.
 */
function RechercheGlobale({ signalFocus = 0, onFermer = () => {} }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [q, setQ] = useState('');
  const [resultat, setResultat] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [ouvert, setOuvert] = useState(false);
  const [actif, setActif] = useState(0);
  const champ = useRef(null);
  const zone = useRef(null);

  // Tous les résultats à plat, pour la navigation au clavier.
  const aplat = useMemo(
    () => (resultat?.groupes || []).flatMap((g) => g.resultats.map((r) => ({ ...r, groupe: g.cle }))),
    [resultat]
  );

  // Changer de page referme la barre (le Layout la démonte). Pas au montage :
  // l'effet y tourne aussi, et refermait la barre à l'instant même où on
  // l'ouvrait (la loupe et Ctrl+K ne faisaient rien).
  const cheminAffiche = useRef(pathname);
  useEffect(() => {
    if (cheminAffiche.current === pathname) return;
    cheminAffiche.current = pathname;
    onFermer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // À l'ouverture, et à chaque nouvel appel : le curseur est dans le champ.
  useEffect(() => {
    champ.current?.focus();
    champ.current?.select();
    setOuvert(true);
  }, [signalFocus]);

  // Clic hors de la barre : elle se referme. La loupe de la sidebar n'en
  // fait pas partie — son clic la rappelle, sans la fermer d'abord.
  useEffect(() => {
    const surClic = (e) => {
      if (e.target.closest && e.target.closest('[data-recherche-declencheur]')) return;
      if (zone.current && !zone.current.contains(e.target)) onFermer();
    };
    document.addEventListener('mousedown', surClic);
    return () => document.removeEventListener('mousedown', surClic);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recherche au fil de la frappe, avec un temps d'arrêt.
  useEffect(() => {
    const texte = q.trim();
    if (texte.length < 2) {
      setResultat(null);
      setErreur(null);
      setChargement(false);
      return undefined;
    }
    let vivant = true;
    setChargement(true);
    const minuteur = setTimeout(() => {
      rechercher(texte)
        .then((res) => {
          if (!vivant) return;
          setResultat(res.data);
          setErreur(null);
          setActif(0);
        })
        .catch((err) => vivant && setErreur(err.message || 'Recherche impossible.'))
        .finally(() => vivant && setChargement(false));
    }, 250);
    return () => {
      vivant = false;
      clearTimeout(minuteur);
    };
  }, [q]);

  function ouvrir(r) {
    setOuvert(false);
    setQ('');
    setResultat(null);
    champ.current?.blur();
    onFermer();
    navigate(r.lien);
  }

  function surTouche(e) {
    if (e.key === 'Escape') {
      setOuvert(false);
      champ.current?.blur();
      onFermer();
      return;
    }
    if (!aplat.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOuvert(true);
      setActif((i) => (i + 1) % aplat.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActif((i) => (i - 1 + aplat.length) % aplat.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (aplat[actif]) ouvrir(aplat[actif]);
    }
  }

  const texte = q.trim();
  const afficher = ouvert && texte.length >= 2;
  let rang = -1;

  return (
    <div className="recherche-globale" ref={zone}>
      <div className="recherche-globale-champ">
        <svg
          className="recherche-globale-icone"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <circle cx="11" cy="11" r="7" />
          <path d="M20 20l-3.5-3.5" />
        </svg>
        <input
          ref={champ}
          type="search"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOuvert(true);
          }}
          onFocus={() => setOuvert(true)}
          onKeyDown={surTouche}
          placeholder="Rechercher partout : client, devis, contrat, participant, formateur, formation…"
          role="combobox"
          aria-label="Recherche globale"
          aria-expanded={afficher}
          aria-controls="recherche-globale-liste"
          aria-autocomplete="list"
          autoComplete="off"
        />
        <kbd className="recherche-globale-raccourci" aria-hidden="true">
          Ctrl K
        </kbd>
        <button
          type="button"
          className="recherche-globale-fermer"
          onClick={onFermer}
          aria-label="Fermer la recherche"
          title="Fermer (Échap)"
        >
          ×
        </button>
      </div>

      {afficher && (
        <div className="recherche-globale-liste" id="recherche-globale-liste" role="listbox">
          {chargement && !resultat && <div className="recherche-globale-etat">Recherche…</div>}
          {erreur && <div className="recherche-globale-etat recherche-globale-erreur">{erreur}</div>}
          {resultat && resultat.total === 0 && !chargement && (
            <div className="recherche-globale-etat">Aucun résultat pour « {texte} ».</div>
          )}
          {(resultat?.groupes || []).map((g) => (
            <div className="recherche-globale-groupe" key={g.cle}>
              <div className="recherche-globale-groupe-titre">
                {g.libelle}
                {g.plus && <span> — les premiers résultats ; précisez la recherche</span>}
              </div>
              {g.resultats.map((r) => {
                rang += 1;
                const i = rang;
                return (
                  <button
                    type="button"
                    key={`${g.cle}-${r.id}`}
                    className={`recherche-globale-item${i === actif ? ' recherche-globale-item-actif' : ''}`}
                    onMouseEnter={() => setActif(i)}
                    onClick={() => ouvrir(r)}
                    role="option"
                    aria-selected={i === actif}
                  >
                    <span className="recherche-globale-item-titre">{r.titre}</span>
                    {r.detail && <span className="recherche-globale-item-detail">{r.detail}</span>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default RechercheGlobale;
